import { useCallback, useState, useRef } from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import { Camera, X, Upload, ImagePlus, Palette, Eye, EyeOff, Save, ChevronDown, ChevronUp, RotateCw, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import type { AnnouncementFormData } from '@/types/announcement';
import { CustomLayoutRenderer } from '@/components/wizard/custom-layouts/CustomLayoutRenderer';
import { TemplateEditorDrawer } from '@/components/wizard/TemplateEditorDrawer';
import { LayoutFamilyPicker } from '@/components/wizard/LayoutFamilyPicker';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

const copy = {
  en: {
    title: 'Design Your Message',
    subtitle: 'Upload photos, choose a layout, and customize every detail. Watch your design come alive in real-time.',
    photosSection: 'Photos',
    addPhoto: 'Add photo',
    optional: 'Optional',
    upload: 'Upload Photos',
    fileNote: 'JPG, PNG, WebP or GIF. Max 5MB each.',
    layoutSection: 'Choose Layout Style',
    livePreview: 'Live Preview',
    hidePreview: 'Hide Preview',
    showPreview: 'Show Preview',
    customize: 'Customize Design',
    customizeHint: '✨ Tap the toolbar below to customize layout, colors, fonts, photos, and decorations.',
    saveTemplate: 'Save as Template',
    saveTemplateTitle: 'Save Custom Template',
    templateName: 'Template Name',
    templateNamePlaceholder: 'e.g. My Romantic Style',
    saving: 'Saving...',
    saved: 'Template saved! Find it in your templates.',
    saveFail: 'Failed to save template',
    dragHint: 'Hold & drag to reorder photos',
    photoEffects: 'Photo Effects',
    tiltEffect: 'Tilt',
    fadeEffect: 'Soft Fade',
    noEffect: 'Normal',
  },
  es: {
    title: 'Diseña Tu Mensaje',
    subtitle: 'Sube fotos, elige un diseño y personaliza cada detalle. Mira tu diseño cobrar vida en tiempo real.',
    photosSection: 'Fotos',
    addPhoto: 'Agregar foto',
    optional: 'Opcional',
    upload: 'Subir Fotos',
    fileNote: 'JPG, PNG, WebP o GIF. Máx 5MB cada una.',
    layoutSection: 'Elegir Estilo de Diseño',
    livePreview: 'Vista Previa en Vivo',
    hidePreview: 'Ocultar Vista Previa',
    showPreview: 'Mostrar Vista Previa',
    customize: 'Personalizar Diseño',
    customizeHint: '✨ Toca la barra de herramientas para personalizar diseño, colores, fuentes, fotos y decoraciones.',
    saveTemplate: 'Guardar como Plantilla',
    saveTemplateTitle: 'Guardar Plantilla Personalizada',
    templateName: 'Nombre de la Plantilla',
    templateNamePlaceholder: 'ej. Mi Estilo Romántico',
    saving: 'Guardando...',
    saved: '¡Plantilla guardada! Encuéntrala en tus plantillas.',
    saveFail: 'Error al guardar plantilla',
    dragHint: 'Mantén presionado y arrastra para reordenar fotos',
    photoEffects: 'Efectos de Foto',
    tiltEffect: 'Inclinación',
    fadeEffect: 'Desvanecimiento',
    noEffect: 'Normal',
  },
  fr: { title: 'Concevez Votre Message', subtitle: 'Téléchargez des photos, choisissez une mise en page et personnalisez chaque détail.', photosSection: 'Photos', addPhoto: 'Ajouter une photo', optional: 'Optionnel', upload: 'Télécharger les Photos', fileNote: 'JPG, PNG, WebP ou GIF. Max 5 Mo.', layoutSection: 'Choisir le Style', livePreview: 'Aperçu en Direct', hidePreview: 'Masquer', showPreview: 'Afficher', customize: 'Personnaliser', customizeHint: '✨ Utilisez la barre pour personnaliser.', saveTemplate: 'Sauvegarder le Modèle', saveTemplateTitle: 'Sauvegarder le Modèle', templateName: 'Nom du Modèle', templateNamePlaceholder: 'ex. Mon Style Romantique', saving: 'Sauvegarde...', saved: 'Modèle sauvegardé !', saveFail: 'Échec de la sauvegarde', dragHint: 'Glissez pour réordonner', photoEffects: 'Effets Photo', tiltEffect: 'Inclinaison', fadeEffect: 'Fondu', noEffect: 'Normal' },
  pt: { title: 'Desenhe Sua Mensagem', subtitle: 'Envie fotos, escolha um layout e personalize cada detalhe.', photosSection: 'Fotos', addPhoto: 'Adicionar foto', optional: 'Opcional', upload: 'Enviar Fotos', fileNote: 'JPG, PNG, WebP ou GIF. Máx 5MB.', layoutSection: 'Escolher Estilo', livePreview: 'Prévia ao Vivo', hidePreview: 'Ocultar', showPreview: 'Mostrar', customize: 'Personalizar', customizeHint: '✨ Toque na barra para personalizar.', saveTemplate: 'Salvar como Modelo', saveTemplateTitle: 'Salvar Modelo', templateName: 'Nome do Modelo', templateNamePlaceholder: 'ex. Meu Estilo Romântico', saving: 'Salvando...', saved: 'Modelo salvo!', saveFail: 'Falha ao salvar', dragHint: 'Arraste para reordenar', photoEffects: 'Efeitos de Foto', tiltEffect: 'Inclinação', fadeEffect: 'Desvanecer', noEffect: 'Normal' },
  ar: { title: 'صمم رسالتك', subtitle: 'ارفع صوراً واختر تصميماً وخصص كل التفاصيل.', photosSection: 'الصور', addPhoto: 'إضافة صورة', optional: 'اختياري', upload: 'رفع الصور', fileNote: 'JPG, PNG, WebP أو GIF. بحد أقصى 5 ميغا.', layoutSection: 'اختر نمط التصميم', livePreview: 'معاينة مباشرة', hidePreview: 'إخفاء', showPreview: 'إظهار', customize: 'تخصيص', customizeHint: '✨ اضغط على الشريط للتخصيص.', saveTemplate: 'حفظ كقالب', saveTemplateTitle: 'حفظ القالب', templateName: 'اسم القالب', templateNamePlaceholder: 'مثال: أسلوبي الرومانسي', saving: 'جارٍ الحفظ...', saved: 'تم حفظ القالب!', saveFail: 'فشل الحفظ', dragHint: 'اسحب لإعادة الترتيب', photoEffects: 'تأثيرات الصور', tiltEffect: 'ميل', fadeEffect: 'تلاشي', noEffect: 'عادي' },
  ja: { title: 'メッセージをデザイン', subtitle: '写真をアップロードし、レイアウトを選び、すべてをカスタマイズ。', photosSection: '写真', addPhoto: '写真を追加', optional: '任意', upload: 'アップロード', fileNote: 'JPG, PNG, WebP, GIF。各最大5MB。', layoutSection: 'レイアウトスタイル', livePreview: 'ライブプレビュー', hidePreview: '非表示', showPreview: '表示', customize: 'カスタマイズ', customizeHint: '✨ ツールバーでカスタマイズ。', saveTemplate: 'テンプレート保存', saveTemplateTitle: 'テンプレートを保存', templateName: 'テンプレート名', templateNamePlaceholder: '例: ロマンチックスタイル', saving: '保存中...', saved: 'テンプレートを保存しました！', saveFail: '保存に失敗しました', dragHint: 'ドラッグで並べ替え', photoEffects: 'フォトエフェクト', tiltEffect: '傾き', fadeEffect: 'フェード', noEffect: '通常' },
  ko: { title: '메시지 디자인', subtitle: '사진을 업로드하고 레이아웃을 선택하고 모든 세부사항을 커스터마이즈하세요.', photosSection: '사진', addPhoto: '사진 추가', optional: '선택사항', upload: '사진 업로드', fileNote: 'JPG, PNG, WebP 또는 GIF. 각 최대 5MB.', layoutSection: '레이아웃 스타일', livePreview: '실시간 미리보기', hidePreview: '숨기기', showPreview: '보기', customize: '커스터마이즈', customizeHint: '✨ 도구 모음에서 커스터마이즈하세요.', saveTemplate: '템플릿 저장', saveTemplateTitle: '사용자 정의 템플릿 저장', templateName: '템플릿 이름', templateNamePlaceholder: '예: 나만의 로맨틱 스타일', saving: '저장 중...', saved: '템플릿이 저장되었습니다!', saveFail: '저장 실패', dragHint: '끌어서 순서 변경', photoEffects: '사진 효과', tiltEffect: '기울기', fadeEffect: '페이드', noEffect: '일반' },
  id: { title: 'Desain Pesan Anda', subtitle: 'Unggah foto, pilih tata letak, dan sesuaikan setiap detail.', photosSection: 'Foto', addPhoto: 'Tambah foto', optional: 'Opsional', upload: 'Unggah Foto', fileNote: 'JPG, PNG, WebP atau GIF. Maks 5MB.', layoutSection: 'Pilih Gaya Tata Letak', livePreview: 'Pratinjau Langsung', hidePreview: 'Sembunyikan', showPreview: 'Tampilkan', customize: 'Sesuaikan', customizeHint: '✨ Ketuk bilah alat untuk menyesuaikan.', saveTemplate: 'Simpan sebagai Template', saveTemplateTitle: 'Simpan Template Kustom', templateName: 'Nama Template', templateNamePlaceholder: 'mis. Gaya Romantis Saya', saving: 'Menyimpan...', saved: 'Template tersimpan!', saveFail: 'Gagal menyimpan', dragHint: 'Tahan dan seret untuk mengurutkan ulang', photoEffects: 'Efek Foto', tiltEffect: 'Miring', fadeEffect: 'Pudar', noEffect: 'Normal' },

  hi: {
    title: 'अपना संदेश डिज़ाइन करें',
    subtitle: 'फ़ोटो अपलोड करें, एक लेआउट चुनें, और हर विवरण को अनुकूलित करें। अपने डिज़ाइन को वास्तविक समय में जीवंत होते देखें।',
    photosSection: 'फ़ोटो',
    addPhoto: 'फ़ोटो जोड़ें',
    optional: 'वैकल्पिक',
    upload: 'फ़ोटो अपलोड करें',
    fileNote: 'JPG, PNG, WebP या GIF। प्रत्येक अधिकतम 5MB।',
    layoutSection: 'लेआउट शैली चुनें',
    livePreview: 'लाइव पूर्वावलोकन',
    hidePreview: 'पूर्वावलोकन छिपाएँ',
    showPreview: 'पूर्वावलोकन दिखाएँ',
    customize: 'डिज़ाइन अनुकूलित करें',
    customizeHint: '✨ लेआउट, रंग, फ़ॉन्ट, फ़ोटो और सजावट को अनुकूलित करने के लिए नीचे टूलबार पर टैप करें।',
    saveTemplate: 'टेम्पलेट के रूप में सहेजें',
    saveTemplateTitle: 'कस्टम टेम्पलेट सहेजें',
    templateName: 'टेम्पलेट का नाम',
    templateNamePlaceholder: 'उदाहरण के लिए मेरा रोमांटिक स्टाइल',
    saving: 'सहेजा जा रहा है...',
    saved: 'टेम्पलेट सहेजा गया! इसे अपने टेम्पलेट्स में खोजें।',
    saveFail: 'टेम्पलेट सहेजने में विफल रहा',
    dragHint: 'फ़ोटो को फिर से व्यवस्थित करने के लिए दबाकर खींचें',
    photoEffects: 'फ़ोटो प्रभाव',
    tiltEffect: 'झुकाव',
    fadeEffect: 'हल्का फ़ेड',
    noEffect: 'सामान्य',
  },
};

type PhotoEffect = 'none' | 'tilt' | 'fade';

interface CustomDesignStepProps {
  formData: AnnouncementFormData;
  updateFormData: (updates: Partial<AnnouncementFormData>) => void;
  existingPhotoUrls?: string[];
  onRemoveExistingPhoto?: (index: number) => void;
}

export function CustomDesignStep({
  formData,
  updateFormData,
  existingPhotoUrls = [],
  onRemoveExistingPhoto,
}: CustomDesignStepProps) {
  const { language } = useLanguage();
  const { user } = useAuth();
  const c = lc(copy, language);
  const [showPreview, setShowPreview] = useState(true);
  const [photosExpanded, setPhotosExpanded] = useState(true);
  const [layoutExpanded, setLayoutExpanded] = useState(true);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [photoEffects, setPhotoEffects] = useState<PhotoEffect[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const totalPhotos = existingPhotoUrls.length + formData.photoFiles.length;
  const allPhotoUrls = [...existingPhotoUrls, ...formData.photoUrls.slice(existingPhotoUrls.length)];

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      if (files.length === 0) return;
      const remainingSlots = 3 - existingPhotoUrls.length;
      const newFiles = [...formData.photoFiles, ...files].slice(0, remainingSlots);
      const currentPreviewUrls = formData.photoUrls.slice(existingPhotoUrls.length);
      const newUrls = newFiles.map((file, index) =>
        formData.photoFiles[index] === file && currentPreviewUrls[index]
          ? currentPreviewUrls[index]
          : URL.createObjectURL(file)
      );
      updateFormData({ photoFiles: newFiles, photoUrls: [...existingPhotoUrls, ...newUrls] });
      e.target.value = '';
      // Initialize photo effects for new photos
      setPhotoEffects(prev => {
        const updated = [...prev];
        while (updated.length < existingPhotoUrls.length + newFiles.length) updated.push('none');
        return updated;
      });
    },
    [formData.photoFiles, formData.photoUrls, updateFormData, existingPhotoUrls],
  );

  const removeNewPhoto = useCallback(
    (newIndex: number) => {
      const newFiles = formData.photoFiles.filter((_, i) => i !== newIndex);
      const urlIndex = existingPhotoUrls.length + newIndex;
      if (formData.photoUrls[urlIndex]) URL.revokeObjectURL(formData.photoUrls[urlIndex]);
      const newUrls = formData.photoUrls.slice(existingPhotoUrls.length).filter((_, i) => i !== newIndex);
      updateFormData({ photoFiles: newFiles, photoUrls: [...existingPhotoUrls, ...newUrls] });
      setPhotoEffects(prev => prev.filter((_, i) => i !== urlIndex));
    },
    [formData, updateFormData, existingPhotoUrls],
  );

  const openPhotoPicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const cyclePhotoEffect = (index: number) => {
    setPhotoEffects(prev => {
      const updated = [...prev];
      while (updated.length <= index) updated.push('none');
      const cycle: PhotoEffect[] = ['none', 'tilt', 'fade'];
      const current = cycle.indexOf(updated[index]);
      updated[index] = cycle[(current + 1) % cycle.length];
      return updated;
    });
  };

  const getPhotoEffectClass = (index: number) => {
    const effect = photoEffects[index] || 'none';
    switch (effect) {
      case 'tilt': return 'rotate-3 hover:rotate-0 transition-transform duration-300';
      case 'fade': return 'opacity-80 hover:opacity-100 transition-opacity duration-300';
      default: return '';
    }
  };

  const getPhotoEffectLabel = (index: number) => {
    const effect = photoEffects[index] || 'none';
    switch (effect) {
      case 'tilt': return c.tiltEffect;
      case 'fade': return c.fadeEffect;
      default: return c.noEffect;
    }
  };

  const handleSaveTemplate = async () => {
    if (!user || !templateName.trim()) return;
    setIsSaving(true);
    try {
      const { error } = await supabase.from('saved_custom_templates' as any).insert({
        user_id: user.id,
        name: templateName.trim(),
        settings: formData.customSettings as any,
      } as any);
      if (error) throw error;
      toast.success(c.saved);
      setSaveDialogOpen(false);
      setTemplateName('');
    } catch (err) {
      console.error('Save template error:', err);
      toast.error(c.saveFail);
    }
    setIsSaving(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6 pb-24"
    >
      {/* Header */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 text-primary mb-4">
          <Palette className="h-8 w-8" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-2">{c.title}</h2>
        <p className="text-muted-foreground max-w-md mx-auto text-sm">{c.subtitle}</p>
      </div>

      {/* ─── LIVE PREVIEW ─── */}
      <div className="space-y-2">
        <button
          onClick={() => setShowPreview(!showPreview)}
          className="flex items-center gap-2 text-sm font-semibold text-foreground w-full"
        >
          {showPreview ? <Eye className="h-4 w-4 text-primary" /> : <EyeOff className="h-4 w-4 text-muted-foreground" />}
          {c.livePreview}
          {showPreview ? <ChevronUp className="h-4 w-4 ml-auto" /> : <ChevronDown className="h-4 w-4 ml-auto" />}
        </button>
        <AnimatePresence>
          {showPreview && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="rounded-2xl overflow-hidden shadow-2xl border border-border">
                <CustomLayoutRenderer formData={formData} allPhotoUrls={allPhotoUrls} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ─── PHOTOS SECTION ─── */}
      <div className="space-y-3 bg-card rounded-xl border border-border p-4">
        <button
          onClick={() => setPhotosExpanded(!photosExpanded)}
          className="flex items-center gap-2 text-sm font-semibold text-foreground w-full"
        >
          <Camera className="h-4 w-4 text-primary" />
          {c.photosSection}
          <span className="text-xs text-muted-foreground ml-1">({totalPhotos}/3)</span>
          {photosExpanded ? <ChevronUp className="h-4 w-4 ml-auto" /> : <ChevronDown className="h-4 w-4 ml-auto" />}
        </button>

        <AnimatePresence>
          {photosExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden space-y-3"
            >
              {/* Photo grid */}
              <div className="grid grid-cols-3 gap-3">
                {[0, 1, 2].map((index) => {
                  const isExisting = index < existingPhotoUrls.length;
                  const newFileIndex = index - existingPhotoUrls.length;
                  const hasPhoto = isExisting || newFileIndex < formData.photoFiles.length;
                  const photoUrl = formData.photoUrls[index] || null;

                  return (
                    <motion.div
                      key={index}
                      className={cn(
                        'aspect-square rounded-xl border-2 border-dashed transition-all overflow-visible relative',
                        hasPhoto ? 'border-primary bg-primary/5' : 'border-muted-foreground/30 hover:border-primary/50 hover:bg-muted/50',
                      )}
                      whileHover={{ scale: 1.02 }}
                      layout
                    >
                      {hasPhoto && photoUrl ? (
                        <div className={cn('w-full h-full rounded-xl overflow-hidden relative', getPhotoEffectClass(index))}>
                          <img src={photoUrl} alt={`Photo ${index + 1}`} className="w-full h-full object-cover" />
                          {/* Effect toggle button */}
                          <button
                            onClick={(e) => { e.stopPropagation(); cyclePhotoEffect(index); }}
                            className="absolute bottom-1 left-1 flex items-center gap-1 px-1.5 py-0.5 rounded bg-black/50 text-white text-[9px] font-medium hover:bg-black/70 transition-colors"
                            title={c.photoEffects}
                          >
                            <RotateCw className="h-2.5 w-2.5" />
                            {getPhotoEffectLabel(index)}
                          </button>
                          {/* Background toggle */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const bgIndex = formData.customSettings.backgroundPhotoIndex === index ? null : index;
                              updateFormData({ customSettings: { ...formData.customSettings, backgroundPhotoIndex: bgIndex } });
                            }}
                            className={cn(
                              'absolute bottom-1 right-1 px-1.5 py-0.5 rounded text-[9px] font-medium transition-all',
                              formData.customSettings.backgroundPhotoIndex === index
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-black/50 text-white hover:bg-black/70',
                            )}
                          >
                            {formData.customSettings.backgroundPhotoIndex === index ? 'BG ✓' : 'Set BG'}
                          </button>
                          {/* Remove */}
                          <button
                            onClick={() => {
                              if (isExisting && onRemoveExistingPhoto) onRemoveExistingPhoto(index);
                              else removeNewPhoto(newFileIndex);
                            }}
                            className="absolute top-1.5 right-1.5 p-1 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ) : (
                        <button type="button" onClick={openPhotoPicker} className="flex flex-col items-center justify-center h-full w-full cursor-pointer p-3">
                          <ImagePlus className="h-7 w-7 text-muted-foreground/50 mb-1" />
                          <span className="text-[10px] text-muted-foreground text-center">
                            {totalPhotos === 0 && index === 0 ? c.addPhoto : c.optional}
                          </span>
                        </button>
                      )}
                    </motion.div>
                  );
                })}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                multiple
                onChange={handleFileChange}
                className="hidden"
              />

              {/* Upload button */}
              {totalPhotos < 3 && (
                <div className="text-center">
                  <Button type="button" variant="outline" size="sm" className="gap-2" onClick={openPhotoPicker}>
                    <Upload className="h-3.5 w-3.5" />
                    {c.upload}
                  </Button>
                  <p className="text-[10px] text-muted-foreground mt-1">{c.fileNote}</p>
                </div>
              )}

              {totalPhotos > 1 && (
                <p className="text-[10px] text-muted-foreground text-center">{c.dragHint}</p>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ─── LAYOUT STYLE PICKER ─── */}
      <div className="space-y-3 bg-card rounded-xl border border-border p-4">
        <button
          onClick={() => setLayoutExpanded(!layoutExpanded)}
          className="flex items-center gap-2 text-sm font-semibold text-foreground w-full"
        >
          <Sparkles className="h-4 w-4 text-primary" />
          {c.layoutSection}
          {layoutExpanded ? <ChevronUp className="h-4 w-4 ml-auto" /> : <ChevronDown className="h-4 w-4 ml-auto" />}
        </button>

        <AnimatePresence>
          {layoutExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <LayoutFamilyPicker
                selected={formData.customSettings.layoutFamily}
                onSelect={(family, defaults) => {
                  updateFormData({
                    customSettings: { ...formData.customSettings, ...defaults, layoutFamily: family },
                  });
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ─── CUSTOMIZE HINT + SAVE ─── */}
      <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 space-y-3">
        <p className="text-sm text-foreground/80 text-center">{c.customizeHint}</p>
        <div className="flex justify-center">
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => setSaveDialogOpen(true)}
          >
            <Save className="h-3.5 w-3.5" />
            {c.saveTemplate}
          </Button>
        </div>
      </div>

      {/* ─── TEMPLATE EDITOR DRAWER (fixed bottom toolbar) ─── */}
      <TemplateEditorDrawer
        formData={formData}
        updateFormData={updateFormData}
        isEditing={true}
        onTogglePreview={() => setShowPreview(!showPreview)}
      />

      {/* ─── SAVE TEMPLATE DIALOG ─── */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{c.saveTemplateTitle}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="template-name">{c.templateName}</Label>
              <Input
                id="template-name"
                placeholder={c.templateNamePlaceholder}
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                maxLength={50}
              />
            </div>
            {/* Mini preview */}
            <div className="rounded-lg overflow-hidden border border-border max-h-48">
              <div className="transform scale-[0.35] origin-top-left w-[286%]">
                <CustomLayoutRenderer formData={formData} allPhotoUrls={allPhotoUrls} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveTemplate} disabled={!templateName.trim() || isSaving}>
              {isSaving ? c.saving : c.saveTemplate}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
